import clsx from "clsx";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import CheckMarkIcon from "../Icons/CheckMarkIcon";
import { CircleLoaderIcon } from "../Icons/CircleLoaderIcon/CircleLoaderIcon";
import CloseIcon from "../Icons/CloseIcon";
import InfoIconNoBg from "../Icons/InfoIconNoBg";
import s from "./NotificationItem.module.scss";

interface Props {
  id: number;
  onRemove: (id: number) => void;
  title: string;
  status: "success" | "warning" | "attention";
  setTitle: Dispatch<SetStateAction<string>>;
  setStatus: Dispatch<SetStateAction<"success" | "warning" | "attention">>;
}

enum colors {
  green = "rgba(67, 210, 108, 1)",
  red = "rgba(227, 79, 79, 1)",
  orange = "rgba(233, 190, 80, 1)",
  light_green = "rgba(67, 210, 108, 0.2)",
  light_red = "rgba(227, 79, 79, 0.2)",
  light_orange = "rgba(236, 185, 55, 0.2)",
}

const NotificationItem: React.FC<Props> = ({ id, onRemove, title, status }) => {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
    }, 4000);

    return () => {
      clearTimeout(timer);
    };
  }, [id]);

  useEffect(() => {
    if (!visible) {
      onRemove(id);
    }
  }, [visible, id, onRemove]);

  const circleLoaderBg = useMemo(() => {
    switch (status) {
      case "attention":
        return colors.light_orange;
      case "success":
        return colors.light_green;
      case "warning":
        return colors.light_red;
    }
  }, [status]);

  const circleLoaderStroke = useMemo(() => {
    switch (status) {
      case "attention":
        return colors.orange;
      case "success":
        return colors.green;
      case "warning":
        return colors.red;
    }
  }, [status]);

  return (
    <div className={clsx(s.wrapper, { [s.hidden]: !visible })}>
      <div className={s.info}>
        <div className={clsx(s.icon)}>
          <CircleLoaderIcon
            stroke={circleLoaderStroke}
            background={circleLoaderBg}
          />
          <div
            className={clsx(
              s.icon_inner,
              status === "attention" && s.orange,
              status === "success" && s.green
            )}
          >
            {status === "success" && <CheckMarkIcon color={colors.green} />}
            {status === "warning" && <CloseIcon color={colors.red} />}
            {status === "attention" && <InfoIconNoBg color={colors.orange} />}
          </div>
        </div>
        <div className={s.content}>
          <div className={s.title}>{title}</div>
        </div>
      </div>
      <div
        className={s.close}
        onClick={() => {
          onRemove(id);
        }}
      >
        <CloseIcon />
      </div>
    </div>
  );
};

export default NotificationItem;
